import { defineStore } from "pinia"

import { APIEvent } from "@api/event.api"

import { type APIError } from "@interfaces/APIError.interface"
import { type Event } from "@utils/interfaces/Event.interface"

export const useEventStore = defineStore("event", {
  state: () => {
    return {
      hasEvents: false,
      events: [] as Event[]
    }
  },
  getters: {
    getEventsDatesOnce: state => {
      const datesMap = new Map<
        string,
        {
          date: Date
          agendas: {
            id: number
            name: string | null
            image: string | null
            company: string | null
            subdomain: string | null
            events: {
              id: number
              name: string
              dateStart: Date
              confirmation: string
              price: number
              services: Event["services"] | null
              eventGroupId?: number | null
              waitingListPosition?: number | null
              waitingListTotal?: number | null
              waitingListPreferredInterval?: string | null
            }[]
          }[]
        }
      >()

      state.events.forEach(event => {
        const dateKey = event.dateStart.toDateString()
        if (!datesMap.has(dateKey)) {
          datesMap.set(dateKey, { date: new Date(dateKey), agendas: [] })
        }

        const dateGroup = datesMap.get(dateKey)
        if (dateGroup) {
          const agendaId = event.agenda?.id || null

          // Check if an agenda with the same name already exists
          const existingAgenda = dateGroup.agendas.find(agenda => agenda.id === agendaId)

          if (existingAgenda) {
            // Add event to existing agenda
            existingAgenda.events.push({
              id: event.id,
              name: event.name,
              dateStart: event.dateStart,
              confirmation: event.confirmation,
              price: event.price,
              services: event.services,
              eventGroupId: event.eventGroupId ?? null,
              waitingListPosition: event.waitingListPosition ?? null,
              waitingListTotal: event.waitingListTotal ?? null,
              waitingListPreferredInterval: event.waitingListPreferredInterval ?? null
            })
          } else {
            // Create new agenda entry
            const agendaName = event.agenda?.name || null
            const agendaImage = event.agenda?.image || null
            const companyName = event.company?.name || null
            const companySubdomain = event.company?.subdomain || null

            dateGroup.agendas.push({
              id: agendaId || 0,
              name: agendaName,
              image: agendaImage,
              company: companyName,
              subdomain: companySubdomain,
              events: [
                {
                  id: event.id,
                  name: event.name,
                  dateStart: event.dateStart,
                  confirmation: event.confirmation,
                  price: event.price,
                  services: event.services,
                  eventGroupId: event.eventGroupId ?? null,
                  waitingListPosition: event.waitingListPosition ?? null,
                  waitingListTotal: event.waitingListTotal ?? null,
                  waitingListPreferredInterval: event.waitingListPreferredInterval ?? null
                }
              ]
            })
          }
        }
      })

      return Array.from(datesMap.values())
    }
  },
  actions: {
    async fetchEvents(): Promise<Event[] | APIError> {
      const response = await APIEvent.getAllEvents()
      if ("error" in response) return { error: response.error }

      if (!response.events.length) {
        this.hasEvents = false
        this.events = []
        return []
      }

      // Convert date strings to Date objects
      const eventsWithDates = response.events.map((event: Event) => ({
        ...event,
        dateStart: new Date(event.dateStart)
      })) as Event[]

      this.hasEvents = true
      this.events = eventsWithDates
      return eventsWithDates
    },
    /**
     * Update the confirmation status for a single event in the store.
     * This keeps mutations inside the store (safer for tracking & linting).
     */
    updateEventConfirmation(eventId: number, confirmation: string) {
      const exists = this.events.some(e => e.id === eventId)
      if (!exists) return

      this.$patch({
        events: this.events.map(e => (e.id === eventId ? { ...e, confirmation } : e))
      })
    }
  }
})
